const categoryActivityStorageKey = 'english-mini-app/category-activity';
const categoryScrollStorageKey = 'english-mini-app/category-scroll';

const emptyCategoryActivity = {
  lastOpenedCategoryId: 0,
  viewedWordIDsByCategory: {},
  trainingCountByCategory: {}
};

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function parseStoredJSON(storage, key, fallbackValue) {
  if (!storage) {
    return fallbackValue;
  }

  try {
    const raw = storage.getItem(key);
    if (!raw) {
      return fallbackValue;
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : fallbackValue;
  } catch (error) {
    return fallbackValue;
  }
}

function normalizeActivity(value) {
  return {
    lastOpenedCategoryId: Number(value?.lastOpenedCategoryId) || 0,
    viewedWordIDsByCategory: value?.viewedWordIDsByCategory && typeof value.viewedWordIDsByCategory === 'object'
      ? value.viewedWordIDsByCategory
      : {},
    trainingCountByCategory: value?.trainingCountByCategory && typeof value.trainingCountByCategory === 'object'
      ? value.trainingCountByCategory
      : {}
  };
}

function persistActivity(activity) {
  if (!canUseStorage()) {
    return activity;
  }

  window.localStorage.setItem(categoryActivityStorageKey, JSON.stringify(activity));
  return activity;
}

export function loadCategoryActivity() {
  if (!canUseStorage()) {
    return emptyCategoryActivity;
  }

  return normalizeActivity(parseStoredJSON(window.localStorage, categoryActivityStorageKey, emptyCategoryActivity));
}

export function recordCategoryOpened(categoryId) {
  const current = loadCategoryActivity();
  return persistActivity({
    ...current,
    lastOpenedCategoryId: categoryId
  });
}

export function recordCategoryWordViewed(categoryId, wordId) {
  const current = loadCategoryActivity();
  const currentItems = Array.isArray(current.viewedWordIDsByCategory[categoryId])
    ? current.viewedWordIDsByCategory[categoryId]
    : [];
  const nextItems = currentItems.includes(wordId) ? currentItems : currentItems.concat(wordId);

  return persistActivity({
    ...current,
    viewedWordIDsByCategory: {
      ...current.viewedWordIDsByCategory,
      [categoryId]: nextItems
    }
  });
}

export function recordCategoryTraining(categoryId) {
  const current = loadCategoryActivity();
  const currentCount = Number(current.trainingCountByCategory[categoryId]) || 0;

  return persistActivity({
    ...current,
    trainingCountByCategory: {
      ...current.trainingCountByCategory,
      [categoryId]: currentCount + 1
    }
  });
}

export function getViewedWordIDs(activity, categoryId) {
  if (!categoryId) {
    return [];
  }

  const items = activity?.viewedWordIDsByCategory?.[categoryId];
  return Array.isArray(items) ? items : [];
}

export function getTrainingCount(activity, categoryId) {
  return Number(activity?.trainingCountByCategory?.[categoryId]) || 0;
}

export function getCategoryProgress(activity, category) {
  const wordCount = Number(category?.word_count) || 0;
  const viewedCount = Math.min(getViewedWordIDs(activity, category?.id).length, wordCount);
  const trainingCount = getTrainingCount(activity, category?.id);
  const percent = wordCount > 0 ? Math.round((viewedCount / wordCount) * 100) : 0;

  return {
    viewedCount,
    trainingCount,
    percent,
    hasStarted: viewedCount > 0 || trainingCount > 0,
    hasCompletedPreview: wordCount > 0 && viewedCount >= wordCount
  };
}

export function getDifficultyMeta(value) {
  switch (String(value || '0')) {
    case '2':
      return { label: 'Продвинутый' };
    case '1':
      return { label: 'Средний' };
    default:
      return { label: 'Легкий старт' };
  }
}

export function getCategoryDescription(category) {
  if (category?.description) {
    return category.description;
  }

  return `Тематическая подборка, чтобы быстрее закрепить лексику по теме "${category?.name || 'Категория'}".`;
}

export function getCategoryRecommendation(category, activity) {
  const progress = getCategoryProgress(activity, category);
  const wordCount = Number(category?.word_count) || 0;

  if (activity?.lastOpenedCategoryId === category?.id && progress.hasStarted) {
    return 'Продолжить с того места, где остановились';
  }
  if (!progress.hasStarted && wordCount > 0 && wordCount <= 12) {
    return 'Короткая тема для быстрого старта';
  }
  if (!progress.hasStarted && String(category?.difficulty || '0') === '0') {
    return 'Подходит, чтобы начать без перегруза';
  }
  if (progress.hasStarted && progress.percent < 100) {
    return `Осталось ${Math.max(wordCount - progress.viewedCount, 0)} ${declineWords(Math.max(wordCount - progress.viewedCount, 0))}`;
  }
  if (progress.trainingCount > 0) {
    return `${progress.trainingCount} ${declineTraining(progress.trainingCount)} по теме`;
  }
  return 'Откройте тему и выберите слова для словаря';
}

function declineWords(count) {
  const n = Math.abs(count) % 100;
  const n1 = n % 10;
  if (n > 10 && n < 20) {
    return 'слов';
  }
  if (n1 > 1 && n1 < 5) {
    return 'слова';
  }
  if (n1 === 1) {
    return 'слово';
  }
  return 'слов';
}

function declineTraining(count) {
  const n = Math.abs(count) % 100;
  const n1 = n % 10;
  if (n > 10 && n < 20) {
    return 'тренировок';
  }
  if (n1 > 1 && n1 < 5) {
    return 'тренировки';
  }
  if (n1 === 1) {
    return 'тренировка';
  }
  return 'тренировок';
}

export function saveCategoryScrollPosition(categoryId, value) {
  if (!categoryId || !canUseStorage()) {
    return;
  }

  const current = parseStoredJSON(window.sessionStorage, categoryScrollStorageKey, {});
  window.sessionStorage.setItem(categoryScrollStorageKey, JSON.stringify({
    ...current,
    [categoryId]: Math.max(0, Math.round(value))
  }));
}

export function loadCategoryScrollPosition(categoryId) {
  if (!categoryId || !canUseStorage()) {
    return 0;
  }

  const current = parseStoredJSON(window.sessionStorage, categoryScrollStorageKey, {});
  return Number(current?.[categoryId]) || 0;
}

export function consumeCategoryScrollPosition(categoryId) {
  if (!categoryId || !canUseStorage()) {
    return 0;
  }

  const current = parseStoredJSON(window.sessionStorage, categoryScrollStorageKey, {});
  const value = Number(current?.[categoryId]) || 0;
  if (Object.prototype.hasOwnProperty.call(current, categoryId)) {
    delete current[categoryId];
    window.sessionStorage.setItem(categoryScrollStorageKey, JSON.stringify(current));
  }
  return value;
}
